import type { ReservationWidget } from '@sevenrooms/api'
import {
  addFbPixelScript,
  addTikTokAnalytics,
  addTwitterAnalyticsScript,
  addSegmentAnalyticsScript,
  addAdrollAnalyticsScript,
} from './configureTrackersJS'

// eslint-disable-next-line no-warning-comments
// Todo: Add restaurant_data to the window object
declare const window: Window &
  typeof globalThis &
  Pick<ReservationWidget.Window, 'dataLayer' | 'restaurant_data'> & {
    ttq: { load: (id: string) => void; page: () => void }
    twq: (command: string, id: string) => void
    gtag: (...args: unknown[]) => void
    doNotTrack?: string
    analytics: {
      _writeKey: string
      SNIPPET_VERSION: string
      load: (id: string) => void
      page: () => void
    }
    adroll?: {
      track: (command: string) => void
    }
    fbq: (command: 'track' | 'init' | 'trackSingle', pixelId: string, eventName?: string, data?: object) => void
  }

declare const navigator: Navigator & {
  globalPrivacyControl?: string
  doNotTrack: string
  msDoNotTrack: string
}

export const DO_NOT_TRACK =
  document.cookie.search('gpc=1;') !== -1 ||
  (navigator.globalPrivacyControl && navigator.globalPrivacyControl === '1') ||
  (window.doNotTrack && window.doNotTrack === '1') ||
  (navigator.doNotTrack && (navigator.doNotTrack === 'yes' || navigator.doNotTrack === '1')) ||
  (navigator.msDoNotTrack && navigator.msDoNotTrack === '1')

/**
 * To be used with analytics_settings.sr_google_tags_id and analytics_settings.google_tags_id
 * @param tagsId
 * @returns
 */
export function addGoogleTagManager(tagsId: string) {
  if (DO_NOT_TRACK) {
    return
  }
  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  })
  const firstScript = window.document.getElementsByTagName('script')[0]
  const gtmScriptTag = window.document.createElement('script')
  gtmScriptTag.async = true
  gtmScriptTag.src = `https://www.googletagmanager.com/gtm.js?id=${tagsId}`
  firstScript.parentNode?.insertBefore(gtmScriptTag, firstScript)
}

/**
 * To be used with analytics_settings.google_tag_id
 *  may be deprecated
 * @param gtagId
 * @returns
 */
export function addGtag(tagId: string) {
  if (DO_NOT_TRACK) {
    return
  }
  const firstScript = window.document.getElementsByTagName('script')[0]
  const gtagScript = window.document.createElement('script')
  gtagScript.async = true
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`
  firstScript.parentNode?.insertBefore(gtagScript, firstScript)

  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function (...args: any) {
      window.dataLayer.push(args)
    }
  window.gtag('js', new Date())
  window.gtag('config', tagId)
}

export function addFbPixelTracking(fbPixelIds: string[]) {
  if (DO_NOT_TRACK) {
    return
  }
  addFbPixelScript(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  fbPixelIds.forEach(fbId => {
    window.fbq('init', fbId.trim())
  })
}

export function addTikTokTracking(tiktokId: string) {
  if (DO_NOT_TRACK) {
    return
  }
  addTikTokAnalytics(window, document, 'ttq')
  window.ttq.load(tiktokId)
  window.ttq.page()
}

export function addOneTrustScript(oneTrustId: string) {
  const firstScript = window.document.getElementsByTagName('script')[0]
  const oneTrustScript = document.createElement('script')
  oneTrustScript.src = `https://cdn.cookielaw.org/consent/${oneTrustId}/OtAutoBlock.js`
  oneTrustScript.type = 'text/javascript'
  firstScript.parentNode?.insertBefore(oneTrustScript, firstScript)
  const oneTrustTemplateScript = document.createElement('script')
  oneTrustTemplateScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
  oneTrustTemplateScript.setAttribute('data-document-language', 'true')
  oneTrustTemplateScript.type = 'text/javascript'
  oneTrustTemplateScript.charset = 'UTF-8'
  oneTrustTemplateScript.setAttribute('data-domain-script', oneTrustId)
  firstScript.parentNode?.insertBefore(oneTrustTemplateScript, firstScript)
}

export function addTwitterTracking(twitterId: string) {
  if (DO_NOT_TRACK) {
    return
  }
  addTwitterAnalyticsScript(window, document, 'script')
  window.twq('config', twitterId)
}

export function addAdobeAnalytics(adobeId: string) {
  if (DO_NOT_TRACK) {
    return
  }
  const firstScript = window.document.getElementsByTagName('script')[0]
  const adobeAnalyticsScript = window.document.createElement('script')
  adobeAnalyticsScript.async = true
  adobeAnalyticsScript.src = `//assets.adobedtm.com/${adobeId}`
  firstScript.parentNode?.insertBefore(adobeAnalyticsScript, firstScript)
}

export function addSegmentTracking(segmentId: string) {
  if (DO_NOT_TRACK) {
    return
  }
  addSegmentAnalyticsScript()
  window.analytics._writeKey = segmentId
  window.analytics.SNIPPET_VERSION = '4.13.2'
  window.analytics.load(segmentId)
  window.analytics.page()
}

export function addAdrollAnalytics(adrollAdvId: string, adrollPixId: string) {
  if (DO_NOT_TRACK) {
    return
  }
  addAdrollAnalyticsScript(adrollAdvId, adrollPixId)
  window.adroll?.track('pageView')
}

function getENVFromHost() {
  const hostAddr = window.location.host
  if (/.+?-app\.qa\.sevenrooms\.com/g.test(hostAddr)) {
    return 'qa'
  }
  if (hostAddr.includes('local')) {
    return 'dev'
  }
  return 'prod'
}

export function addTealiumAnalytics(tealiumAccount: string, tealiumProfile: string) {
  if (DO_NOT_TRACK) {
    return
  }
  const firstScript = window.document.getElementsByTagName('script')[0]
  const tealiuuAnalyticsScript = window.document.createElement('script')
  tealiuuAnalyticsScript.async = true
  tealiuuAnalyticsScript.src = `//tags.tiqcdn.com/utag/${tealiumAccount}/${tealiumProfile}/${getENVFromHost()}/utag.sync.js`
  firstScript.parentNode?.insertBefore(tealiuuAnalyticsScript, firstScript)

  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.textContent = `(function(a,b,c,d){
      a='//tags.tiqcdn.com/utag/${tealiumAccount}/${tealiumProfile}/${getENVFromHost()}/utag.js';
      b=document; c='script';
      d=b.createElement(c); d.src=a;
      d.type='text/java'+c; d.async=true;
      a=b.getElementsByTagName(c)[0];
      a.parentNode.insertBefore(d,a);
    })();`
  document.body.appendChild(script)
}
